import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, getSavedCreds } from '../../contexts/AuthContext';
import { startSessionLog } from '../../utils/session';
import { verifyDevice, loadOrganisers, needsPasswordChange, humanReason, thisDeviceId } from '../../utils/authFlow';

// All Login logic — shared by Login.desktop / Login.mobile.
export function useLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  // A message handed over by DeviceGuard / ChangePassword (e.g. "signed out: device reset").
  const [err, setErr] = useState(state?.msg || '');
  // Show a "signing in" splash while we re-verify saved credentials on boot.
  const [autoChecking, setAutoChecking] = useState(() => !!getSavedCreds());

  // Verify mobile + password + THIS device against the API and route accordingly.
  // `keep` → persist credentials for next launch. Returns true if it handled routing.
  //   ok                → sign in (binds an unbound account to this device)
  //   device_mismatch   → /otp to move the account here (or Super Admin reset)
  //   password == 1234  → sign in, then compulsory /change-password
  // The demo account (utils/demo.js) skips binding and OTP on the server side.
  const verify = useCallback(async (m, p, keep) => {
    const res = await verifyDevice(m, p, { bind: true });
    if (!res?.ok) {
      if (res?.reason === 'device_mismatch') {
        nav('/otp', { state: { mobile: m, password: p, pendingUser: res.user, remember: keep, boundModel: res.boundModel, boundDate: res.boundDate } });
        return true;
      }
      setErr(humanReason(res?.reason)); return false;
    }
    const user = res.user;
    const list = await loadOrganisers(user);
    if (list.length === 0) { setErr('No organiser is assigned to this user yet'); return false; }
    const mustChange = needsPasswordChange(m, p);
    login(user, list[0], { mobile: m, password: p }, keep, mustChange);
    startSessionLog(user, await thisDeviceId()); // open the activity-log session (best-effort)
    nav(mustChange ? '/change-password' : '/app/dashboard', { replace: true });
    return true;
  }, [login, nav]);

  // Auto sign-in on app start using saved credentials.
  useEffect(() => {
    const creds = getSavedCreds();
    if (!creds?.mobile || !creds?.password) { setAutoChecking(false); return; }
    setMobile(creds.mobile);
    setPassword(creds.password);
    let cancelled = false;
    (async () => {
      try {
        await verify(creds.mobile, creds.password, true);
      } catch {
        if (!cancelled) setErr('Auto sign-in failed. Check your connection and sign in again.');
      } finally {
        if (!cancelled) setAutoChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    if (!password) { setErr('Enter your password'); return; }
    setBusy(true);
    try {
      await verify(mobile, password, remember);
    } catch {
      setErr('Login failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const setMobileDigits = (v) => setMobile(String(v || '').replace(/\D/g, ''));

  return {
    mobile, setMobile: setMobileDigits, password, setPassword, remember, setRemember,
    busy, err, autoChecking, submit,
  };
}
